import { createContext, useContext, ReactNode } from "react";
import {
    AssetMaterializationResult,
    EDITORIAL_ASSETS,
    EditorialAsset,
    OverlayRenderParametersResponse
} from "../types/editorialAssetsTypes";
import React from "react";
import {
    getAssetDefaultRenderParameters,
    getEditorialAssets,
    materializeEditorialAsset
} from "@/queries/editorialAssetQueries";
import { flushSync } from 'react-dom';
import {getContentRenderParams} from '../utils/utils';
import { useContentSpecificParams } from "./ContentSpecificParamsContext";
import {getOverlayUniqueId} from "../utils/editor/editorUtils";

// Tipi per il contesto
interface EditorialAssetsContextType {
  editorialAssets: EditorialAsset[],
  setEditorialAssets: React.Dispatch<React.SetStateAction<EditorialAsset[]>>,
  selectedEditorialAssets: EditorialAsset[],
  setSelectedEditorialAssets: React.Dispatch<React.SetStateAction<EditorialAsset[]>>,
  isMaterializing: boolean,
  setIsMaterializing: React.Dispatch<React.SetStateAction<boolean>>,
  removeSelectedEditorialAssets: (editorialAssetId: string) => void,
  editorialAssetMutualSelection: (editorialAsset: EditorialAsset, removeOpening?:boolean) => void
  loadEditorialAssets : (competitionType: string, competitionId: string) => Promise<EditorialAsset[]>
  addEditorialAssetParameterMap: (editorialAssetId: string, params: any) => void
  addEditorialAssetParameterInterleaveMap: (editorialAssetId: string, overlayId:string, ovrelatparams: any, localRenderParams: OverlayRenderParametersResponse) => void
  clearEditorialAssetParameterInterleaveMap: (editorialAssetId: string ) => void
  pruneEditorialAssetParameterInterleaveMap: (editorialAssetId: string, validOverlayIds: string[]) => void
  setEditorialAssetParameterInterleaveUrls: (editorialAssetId: string, urlsByOverlayId: Record<string, string>) => void
  reorderEditorialAssetParameterInterleaveMap: (editorialAssetId: string ) => boolean
  assetRenderParametersMap: Record<string, OverlayRenderParametersResponse>
  changeAssetRenderParameter: (editorialAssetId: string, field: string, value: any) => void
  setAssetRenderParametersMap: (map: Record<string, OverlayRenderParametersResponse>) => void
  resetEditorialAssetSelection: () => void
  renderEditorialAsset: (editorialAssetId: string, contentType: string) => Promise<AssetMaterializationResult>
  renderEditorialInterleaveAsset: (editorialAssetId: string, contentType: string, localRenderParams: any) => Promise<AssetMaterializationResult> 
  renderSelectedEditorialAssets: (contentType: string) => Promise<AssetMaterializationResult[]>
  getAssetDefaultParameters : (editorialAssetId: string, contentType: string) => Promise<OverlayRenderParametersResponse>
}

// Creazione del contesto
const EditorialAssetsContentContext = createContext<EditorialAssetsContextType | undefined>(undefined);

// Hook personalizzato
export const useEditorialAssets = (): EditorialAssetsContextType => {
  const context = useContext(EditorialAssetsContentContext);
  if (!context) {
    throw new Error("useEditorialAssets must be used within a EditorialAssetsProvider");
  }
  return context;
};

// Tipi per le props del provider
interface EditorialAssetsProviderProps {
  children: ReactNode;
}

// Provider
export const EditorialAssetsProvider: React.FC<EditorialAssetsProviderProps> = ({ children }) => {
    const [editorialAssets, setEditorialAssets] = React.useState<EditorialAsset[]>([]);
    const [selectedEditorialAssets, setSelectedEditorialAssets] = React.useState<EditorialAsset[]>([]);
    const [assetRenderParametersMap, setAssetRenderParametersMap] = React.useState<Record<string, OverlayRenderParametersResponse>>({})
    const [isMaterializing, setIsMaterializing] = React.useState<boolean>(false);

    const {contentSpecificParams} = useContentSpecificParams()

    const editorialAssetMutualSelection = (editorialAsset: EditorialAsset, removeOpening= false) => {
        let tempSelected = [...selectedEditorialAssets]
        if(removeOpening) tempSelected = tempSelected.filter(el => el.placement_type !== EDITORIAL_ASSETS.OPENING_COVER)

        const target = tempSelected.find(asset => asset.placement_type === editorialAsset.placement_type)
        if (target){
            tempSelected = tempSelected.filter(asset => asset.id !== target.id)
            if(target.id !== editorialAsset.id){
                tempSelected.push(editorialAsset)
            }
        }else{
            tempSelected.push(editorialAsset)
        }
        setSelectedEditorialAssets(tempSelected)
    }

    const loadEditorialAssets = async (contentType: string, competitionId: string | null) => {
        let tempEditorialAssets: EditorialAsset[] = await getEditorialAssets(contentType, competitionId);
        tempEditorialAssets = tempEditorialAssets.filter(asset => {
            const { margin_sx, margin_dx } = asset.parameters.technical_parameters;
            if (margin_sx === undefined || margin_dx === undefined) return true;
            return (margin_sx + margin_dx) <= asset.duration;
        })
        setEditorialAssets(tempEditorialAssets);
        return tempEditorialAssets
    }

    const addEditorialAssetParameterMap = async(editorialAssetId:string, contentType: string) => {
      if (!assetRenderParametersMap[editorialAssetId]){
        const renderParams = await getAssetDefaultRenderParameters(editorialAssetId, getContentRenderParams(contentType, contentSpecificParams));
        setAssetRenderParametersMap(prev => ({
            ...prev,
            [editorialAssetId]: renderParams
        }));
      }
    }

    const addEditorialAssetParameterInterleaveMap = async(editorialAssetId:string, overlayId:string, contentType: string, localRenderParams: OverlayRenderParametersResponse) => {
        if (!assetRenderParametersMap[editorialAssetId + '_&_' + overlayId]){           
            try{
                setAssetRenderParametersMap(prev => ({
                    ...prev,
                    [editorialAssetId + '_&_' + overlayId]: localRenderParams
                }));
            }catch(e){console.error('Error during fetch of render arameters')}
        }       
    }

    const clearEditorialAssetParameterInterleaveMap = async(editorialAssetId:string) => {
        try{
            setAssetRenderParametersMap((prev) => {
                const updatedMap = { ...prev };
                Object.keys(updatedMap).forEach((key) => {
                    if (key.startsWith(`${editorialAssetId}_&_`) || key === editorialAssetId) {
                        delete updatedMap[key];
                    }
                });

                return updatedMap;
            });
        }catch(e){console.error('Error during fetch of render arameters')}
    }

    const pruneEditorialAssetParameterInterleaveMap = (editorialAssetId: string, validOverlayIds: string[]) => {
        const validKeys = new Set(validOverlayIds.map((overlayId) => `${editorialAssetId}_&_${overlayId}`));
        setAssetRenderParametersMap((prev) => {
            const updatedMap = { ...prev };
            let changed = false;
            Object.keys(updatedMap).forEach((key) => {
                if (key.startsWith(`${editorialAssetId}_&_`) && !validKeys.has(key)) {
                    delete updatedMap[key];
                    changed = true;
                }
            });
            return changed ? updatedMap : prev;
        });
    }

    const setEditorialAssetParameterInterleaveUrls = (editorialAssetId: string, urlsByOverlayId: Record<string, string>) => {
        setAssetRenderParametersMap((prev) => {
            const updatedMap = { ...prev };
            let changed = false;
            Object.entries(urlsByOverlayId).forEach(([overlayId, url]) => {
                const key = `${editorialAssetId}_&_${overlayId}`;
                if (updatedMap[key] && updatedMap[key].url !== url) {
                    updatedMap[key] = { ...updatedMap[key], url };
                    changed = true;
                }
            });
            return changed ? updatedMap : prev;
        });
    }

    const reorderEditorialAssetParameterInterleaveMap = (editorialAssetId: string): boolean => {
        const existingKeysCount = Object.keys(assetRenderParametersMap).filter((key) => key.startsWith(`${editorialAssetId}_&_`)).length;
        if (existingKeysCount < 2) return false;

        const orderedKeys: string[] = [];
        for (let i = 0; i < existingKeysCount; i++) {
            const candidateKey = `${editorialAssetId}_&_${getOverlayUniqueId(`${editorialAssetId}_ASSET_MID_${i}`)}`;
            if (!assetRenderParametersMap[candidateKey]) return false;
            orderedKeys.push(candidateKey);
        }

        const reversedValues = orderedKeys.map((key) => assetRenderParametersMap[key]).reverse();
        setAssetRenderParametersMap((prev) => {
            const updatedMap = { ...prev };
            orderedKeys.forEach((key, index) => {
                updatedMap[key] = reversedValues[index];
            });
            return updatedMap;
        });

        return true;
    }

    const getAssetDefaultParameters = async (editorialAssetId:string, contentType: string) => {
      return  await getAssetDefaultRenderParameters(editorialAssetId, getContentRenderParams(contentType, contentSpecificParams));
    }

    const changeAssetRenderParameter = (editorialAssetId: string, field: string, value: any) => {
        setAssetRenderParametersMap(prev => {
            const current = prev[editorialAssetId];

            if (!current) {
                console.error("You are updating rendering params of an overlay that does not exist");
                return prev;
            }

            return {
                ...prev,
                [editorialAssetId]: {
                    ...current,
                    overlay_params: {
                        ...current.overlay_params,
                        [field]: value
                    }
                }
            };
        });
    }

    const resetEditorialAssetSelection = () => {
      setSelectedEditorialAssets([]);
      setAssetRenderParametersMap({})
    }

    const renderEditorialAsset = async (editorialAssetId: string, contentType: string) => {
        return await materializeEditorialAsset(
            editorialAssetId,
            getContentRenderParams(contentType, contentSpecificParams),
            assetRenderParametersMap[editorialAssetId] ? assetRenderParametersMap[editorialAssetId].overlay_params : {}
        )
    }

    const renderEditorialInterleaveAsset = async (editorialAssetId: string, contentType: string, overlay_params:any) => {
        return await materializeEditorialAsset(
            editorialAssetId,
            getContentRenderParams(contentType, contentSpecificParams),
            overlay_params ? overlay_params : {}
        )
    }

    const removeSelectedEditorialAssets = async (editorialAssetId: string) => {
        setSelectedEditorialAssets((prev) => prev.filter(el => el.id !== editorialAssetId));
    }

    const renderSelectedEditorialAssets = async (contentType: string) => {
        const contentRenderParams = getContentRenderParams(contentType, contentSpecificParams);

        const results = await Promise.all(
            selectedEditorialAssets
                .filter(asset => asset.parameters?.render_parameters?.length > 0)
                .flatMap(asset => {
                    if (asset.placement_type !== EDITORIAL_ASSETS.INTERLEAVE) {
                        const overlayParams = assetRenderParametersMap[asset.id]?.overlay_params || {};

                        return materializeEditorialAsset(asset.id, contentRenderParams, overlayParams)
                            .then((materializedResult: any) => ({
                                ...materializedResult,
                                id: asset.id,
                                placementType: asset.placement_type
                            }));
                    }
                    
                    return Object.entries(assetRenderParametersMap)
                        .filter(([key]) => String(key).includes('_&_') && asset.id === key.split('_&_')[0])
                        .map(([key, renderParam]) => {
                            return materializeEditorialAsset(asset.id, contentRenderParams, renderParam?.overlay_params || {})
                                .then((materializedResult: any) => ({
                                    ...materializedResult,
                                    id: key,
                                    placementType: asset.placement_type
                                }))}
                        );
                })
        );

        flushSync(() => {
            setAssetRenderParametersMap(prev => {
                return Object.fromEntries(
                    Object.entries(prev).map(([key, value]) => {
                        const result = results.find((r: any) => r.id === key);
                        if (!result) return [key, value];
                        return [key, { ...value, placementType: result.placementType, url: result.url }];
                    })
                );
            });
        });

        return results;
    };

    return (
        <EditorialAssetsContentContext.Provider
        value={{
            addEditorialAssetParameterMap,
            addEditorialAssetParameterInterleaveMap,
            clearEditorialAssetParameterInterleaveMap,
            pruneEditorialAssetParameterInterleaveMap,
            setEditorialAssetParameterInterleaveUrls,
            reorderEditorialAssetParameterInterleaveMap,
            loadEditorialAssets,
            editorialAssetMutualSelection,
            editorialAssets, 
            setEditorialAssets,
            selectedEditorialAssets,
            setSelectedEditorialAssets,
            removeSelectedEditorialAssets,
            assetRenderParametersMap,
            isMaterializing,
            setIsMaterializing,
            changeAssetRenderParameter,
            setAssetRenderParametersMap,
            resetEditorialAssetSelection,
            renderEditorialAsset,
            renderEditorialInterleaveAsset,
            renderSelectedEditorialAssets,
            getAssetDefaultParameters
        }}
        >
        {children}
        </EditorialAssetsContentContext.Provider>
    );
};
